import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

/**
 * Who an article is written for.
 *
 * These are the server's own names, not display labels — the API sends `"Internal"`, and mapping it
 * to something friendlier is the view's job. The library is not a permission: it is what the server
 * filters reads by, and the browser only ever receives articles the reader is already entitled to.
 */
/**
 * Whether a person wrote an article or an agent summarized it from an uploaded document.
 *
 * A summary is marked in the tree and on the page, because a compliance policy that was summarized
 * rather than written has to be checked against its source before anyone relies on it.
 */
export const ArticleOriginZ = z.enum(["Authored", "AgentSummarized"]);
export type ArticleOrigin = z.infer<typeof ArticleOriginZ>;

export const KnowledgeBaseLibraryZ = z.enum(["Internal", "External", "AttorneyHubPublic"]);
export type KnowledgeBaseLibrary = z.infer<typeof KnowledgeBaseLibraryZ>;

export const ArticlePublicationZ = z.enum(["Draft", "Published", "Archived"]);
export type ArticlePublication = z.infer<typeof ArticlePublicationZ>;

export type ArticleTopicJSON = z.infer<typeof KnowledgeBaseArticleSchema.ArticleTopicJSONZ>;
export type KnowledgeBaseArticleJSON = z.infer<typeof KnowledgeBaseArticleSchema.ArticleJSONZ>;
export type KnowledgeBaseArticleParams = z.infer<typeof KnowledgeBaseArticleSchema.ArticleZ>;
export type ArticleIndexEntryJSON = z.infer<typeof KnowledgeBaseArticleSchema.IndexEntryJSONZ>;
export type ArticleSearchResultJSON = z.infer<typeof KnowledgeBaseArticleSchema.SearchResultJSONZ>;
export type ArticleRevisionJSON = z.infer<typeof KnowledgeBaseArticleSchema.RevisionJSONZ>;

export default class KnowledgeBaseArticleSchema {
  public static readonly ArticleTopicJSONZ = z.object({
    entityClass: z.string().nullish(),
    entityId: z.string().nullish(),
    context: z.string().nullish(),
  });

  public static readonly ArticleJSONZ = z.object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    body: z.string(),
    library: KnowledgeBaseLibraryZ,
    publication: ArticlePublicationZ,
    categoryId: z.string().nullish(),
    position: z.number(),
    templateKey: z.string().nullish(),
    templateSubject: z.string().nullish(),
    topics: z.array(KnowledgeBaseArticleSchema.ArticleTopicJSONZ),
    attachedDocumentIds: z.array(z.string()),
    origin: ArticleOriginZ,
    sourceDocumentId: z.string().nullish(),
    summarizedBy: z.string().nullish(),
    summarizedAt: z.string().nullish(),
    created: z.string().nullish(),
    updated: z.string().nullish(),
    published: z.string().nullish(),
    currentRevisionNumber: z.number().nullish(),
  });

  /**
   * The navigation tree. Deliberately without a body: drawing the index should not mean
   * downloading every article in the wiki to render one page.
   */
  public static readonly IndexEntryJSONZ = z.object({
    id: z.string(),
    ownerLegalEntityId: z.string(),
    slug: z.string(),
    title: z.string(),
    library: KnowledgeBaseLibraryZ,
    publication: ArticlePublicationZ,
    categoryId: z.string().nullish(),
    position: z.number(),
    templateKey: z.string().nullish(),
    origin: ArticleOriginZ,
  });

  public static readonly SearchResultJSONZ = z.object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    library: KnowledgeBaseLibraryZ,
    /** Whose knowledge base the hit is in — search crosses every library the reader may see. */
    ownerLegalEntityId: z.string(),
    excerpt: z.string(),
  });

  /**
   * A letter rendered with sample values. `unsuppliedPlaceholders` is the point of the preview:
   * it names what would render blank in a real send.
   */
  public static readonly TemplatePreviewJSONZ = z.object({
    subject: z.string(),
    body: z.string(),
    unsuppliedPlaceholders: z.array(z.string()),
    declaredPlaceholders: z.array(z.string()),
  });

  public static readonly RevisionJSONZ = z.object({
    revisionNumber: z.number(),
    kind: z.string(),
    timeStamp: z.string(),
    actorUserId: z.string().nullish(),
    title: z.string(),
    body: z.string(),
    subject: z.string().nullish(),
    note: z.string(),
  });

  public static readonly ArticleZ = z.object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    body: z.string(),
    library: KnowledgeBaseLibraryZ,
    publication: ArticlePublicationZ,
    categoryId: z.string().optional(),
    position: z.number(),
    templateKey: z.string().optional(),
    templateSubject: z.string().optional(),
    topics: z.array(KnowledgeBaseArticleSchema.ArticleTopicJSONZ),
    attachedDocumentIds: z.array(z.string()),
    origin: ArticleOriginZ,
    sourceDocumentId: z.string().optional(),
    summarizedBy: z.string().optional(),
    summarizedAt: z.date().optional(),
    created: z.date().optional(),
    updated: z.date().optional(),
    published: z.date().optional(),
    currentRevisionNumber: z.number().optional(),
  });

  public static validate(values: unknown): KnowledgeBaseArticleParams {
    return parseOrThrow(KnowledgeBaseArticleSchema.ArticleZ, values, {
      logError: true,
      schemaName: "KnowledgeBaseArticle",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): KnowledgeBaseArticleJSON {
    return parseOrThrow(KnowledgeBaseArticleSchema.ArticleJSONZ, values, {
      logError: true,
      schemaName: "KnowledgeBaseArticle",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is KnowledgeBaseArticleParams {
    try {
      KnowledgeBaseArticleSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: KnowledgeBaseArticleJSON): KnowledgeBaseArticleParams {
    const parsed = parseOrThrow(KnowledgeBaseArticleSchema.ArticleJSONZ, data, {
      logError: true,
      schemaName: "KnowledgeBaseArticle",
      operation: "deserialize",
      payloadSample: data,
    });

    return {
      id: parsed.id,
      slug: parsed.slug,
      title: parsed.title,
      body: parsed.body,
      library: parsed.library,
      publication: parsed.publication,
      categoryId: parsed.categoryId ?? undefined,
      position: parsed.position,
      templateKey: parsed.templateKey ?? undefined,
      templateSubject: parsed.templateSubject ?? undefined,
      topics: parsed.topics,
      attachedDocumentIds: parsed.attachedDocumentIds,
      origin: parsed.origin,
      sourceDocumentId: parsed.sourceDocumentId ?? undefined,
      summarizedBy: parsed.summarizedBy ?? undefined,
      summarizedAt: toDate(parsed.summarizedAt),
      created: toDate(parsed.created),
      updated: toDate(parsed.updated),
      published: toDate(parsed.published),
      currentRevisionNumber: parsed.currentRevisionNumber ?? undefined,
    };
  }

  public static serialize(article: KnowledgeBaseArticleParams): KnowledgeBaseArticleJSON {
    const parsed = KnowledgeBaseArticleSchema.validate(article);

    return {
      id: parsed.id,
      slug: parsed.slug,
      title: parsed.title,
      body: parsed.body,
      library: parsed.library,
      publication: parsed.publication,
      categoryId: parsed.categoryId,
      position: parsed.position,
      templateKey: parsed.templateKey,
      templateSubject: parsed.templateSubject,
      topics: parsed.topics,
      attachedDocumentIds: parsed.attachedDocumentIds,
      origin: parsed.origin,
      sourceDocumentId: parsed.sourceDocumentId,
      summarizedBy: parsed.summarizedBy,
      summarizedAt: parsed.summarizedAt?.toISOString(),
      created: parsed.created?.toISOString(),
      updated: parsed.updated?.toISOString(),
      published: parsed.published?.toISOString(),
      currentRevisionNumber: parsed.currentRevisionNumber,
    };
  }
}

function toDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}
